'use client'

import { useState, useEffect } from 'react'
import { useAuth } from '@/store/hooks'
import {
    createIntegration,
    updateIntegration,
    getIntegrationById,
} from '@/lib/api/integrations/integrations-api'
import {
    AuthenticationType,
    WhatToDoWithTheResponse,
} from '@/lib/api/common/interfaces/integration.interface'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import {
    MdArrowBack,
    MdLink,
    MdLock,
    MdCode,
    MdArrowForward,
    MdArrowBack as MdBack,
} from 'react-icons/md'
import AuthenticationFields from './components/authentication-fields'
import PropertiesFields from './components/properties-fields'
import FormSteps from './components/form-steps'
import { BsChevronDown, BsChevronUp } from 'react-icons/bs'
import { useClientLabel } from '@/lib/hooks/use-client-label'

export default function IntegrationFormPage() {
    const { user } = useAuth()
    const { clientLabel } = useClientLabel()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id
    const [loading, setLoading] = useState(false)
    const [loadingIntegration, setLoadingIntegration] = useState(false)
    const [currentStep, setCurrentStep] = useState(1)
    const [showHeaders, setShowHeaders] = useState(false)
    const [showParams, setShowParams] = useState(false)

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        context: '',
        externalUrl: '',
        properties: {} as any,
        headers: {} as any,
        params: {} as any,
        authenticationType: AuthenticationType.NONE,
        authenticationData: {} as any,
        whatToDoWithResponse: WhatToDoWithTheResponse.USE_TO_ANSWER_CLIENT,
    })

    useEffect(() => {
        if (isEditing && id) {
            loadIntegration()
        }
    }, [id, isEditing])

    const loadIntegration = async () => {
        if (!id) return

        setLoadingIntegration(true)
        try {
            const response = await getIntegrationById(parseInt(id, 10))
            if (response.success && response.result) {
                const integration = response.result
                setFormData({
                    name: integration.name,
                    description: integration.description || '',
                    context: integration.context,
                    externalUrl: integration.externalUrl || '',
                    properties: integration.properties || {},
                    headers: integration.headers || {},
                    params: integration.params || {},
                    authenticationType:
                        integration.authenticationType ||
                        AuthenticationType.NONE,
                    authenticationData: integration.authenticationData || {},
                    whatToDoWithResponse:
                        integration.whatToDoWithResponse ||
                        WhatToDoWithTheResponse.USE_TO_ANSWER_CLIENT,
                })
                setShowHeaders(
                    Object.keys(integration.headers || {}).length > 0
                )
                setShowParams(Object.keys(integration.params || {}).length > 0)
            } else {
                toast.error(response.error || 'Erro ao carregar integração')
                navigate('/integrations')
            }
        } catch (error) {
            toast.error('Erro ao carregar integração')
            navigate('/integrations')
        } finally {
            setLoadingIntegration(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        if (!formData.name.trim()) {
            toast.warning('Preencha o nome da integração')
            return
        }

        if (!formData.context.trim()) {
            toast.warning('Preencha o contexto da integração')
            return
        }

        if (!formData.externalUrl.trim()) {
            toast.warning('Preencha a URL externa da primeira chamada')
            return
        }

        if (Object.keys(formData.properties).length === 0) {
            toast.warning(
                'Adicione pelo menos uma propriedade na primeira chamada'
            )
            return
        }

        if (
            !validateAuthentication(
                formData.authenticationType,
                formData.authenticationData
            )
        ) {
            const authTypeNames: Record<AuthenticationType, string> = {
                [AuthenticationType.NONE]: 'Nenhuma',
                [AuthenticationType.BASIC]: 'Basic',
                [AuthenticationType.BEARER]: 'Bearer',
                [AuthenticationType.API_KEY]: 'API Key',
                [AuthenticationType.OAUTH2]: 'OAuth2',
                [AuthenticationType.CLIENT_CREDENTIALS]: 'Client Credentials',
                [AuthenticationType.JWT]: 'JWT',
            }
            toast.warning(
                `Preencha todos os campos obrigatórios da autenticação ${
                    authTypeNames[formData.authenticationType]
                }`
            )
            return
        }

        setLoading(true)

        try {
            const payload: any = {
                name: formData.name.trim(),
                description: formData.description.trim() || undefined,
                context: formData.context.trim(),
                externalUrl: formData.externalUrl.trim(),
                properties: formData.properties,
                headers:
                    Object.keys(formData.headers).length > 0
                        ? formData.headers
                        : undefined,
                params:
                    Object.keys(formData.params).length > 0
                        ? formData.params
                        : undefined,
                authenticationType: formData.authenticationType,
                authenticationData:
                    formData.authenticationType !== AuthenticationType.NONE &&
                    Object.keys(formData.authenticationData).length > 0
                        ? formData.authenticationData
                        : undefined,
                whatToDoWithResponse: formData.whatToDoWithResponse,
                companyId: user.companyId,
            }

            let response

            if (isEditing) {
                response = await updateIntegration(parseInt(id!, 10), payload)
            } else {
                response = await createIntegration(payload)
            }

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Integração atualizada com sucesso'
                        : 'Integração criada com sucesso'
                )
                navigate('/integrations')
            } else {
                toast.error(
                    response.error ||
                        (isEditing
                            ? 'Erro ao atualizar integração'
                            : 'Erro ao criar integração')
                )
            }
        } catch (error) {
            toast.error(
                isEditing
                    ? 'Erro ao atualizar integração'
                    : 'Erro ao criar integração'
            )
        } finally {
            setLoading(false)
        }
    }

    const updateFormField = (field: keyof typeof formData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const steps = [
        { id: 1, title: 'Info' },
        { id: 2, title: 'Props' },
        { id: 3, title: 'Auth' },
        { id: 4, title: 'Resposta' },
    ]

    const totalSteps = steps.length

    const validateAuthentication = (
        authType: AuthenticationType,
        authData: any
    ): boolean => {
        if (authType === AuthenticationType.NONE) {
            return true
        }

        switch (authType) {
            case AuthenticationType.BASIC:
                return authData?.username?.trim() && authData?.password?.trim()

            case AuthenticationType.BEARER:
                return !!authData?.token?.trim()

            case AuthenticationType.API_KEY:
                return !!authData?.apiKey?.trim()

            case AuthenticationType.OAUTH2:
                return (
                    authData?.clientId?.trim() &&
                    authData?.clientSecret?.trim() &&
                    authData?.tokenUrl?.trim()
                )

            case AuthenticationType.CLIENT_CREDENTIALS:
                return (
                    authData?.clientId?.trim() &&
                    authData?.clientSecret?.trim() &&
                    authData?.tokenUrl?.trim()
                )

            case AuthenticationType.JWT:
                return !!authData?.secret?.trim()

            default:
                return true
        }
    }

    const canGoToNextStep = () => {
        if (currentStep === 1) {
            return formData.name.trim() && formData.context.trim()
        }
        if (currentStep === 2) {
            return (
                formData.externalUrl.trim() &&
                Object.keys(formData.properties).length > 0
            )
        }
        if (currentStep === 3) {
            return validateAuthentication(
                formData.authenticationType,
                formData.authenticationData
            )
        }
        if (currentStep === 4) {
            return true
        }
        return false
    }

    const handleNext = (e?: React.MouseEvent) => {
        e?.preventDefault()
        e?.stopPropagation()
        if (canGoToNextStep() && currentStep < totalSteps) {
            setCurrentStep(currentStep + 1)
        }
    }

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1)
        }
    }

    const handleStepClick = (step: number) => {
        // Só permite ir para steps anteriores ou o atual
        if (step <= currentStep) {
            setCurrentStep(step)
        }
    }

    if (loadingIntegration) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando integração...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/integrations')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <h1 className="text-2xl font-bold text-base-content !mb-0">
                    {isEditing ? 'Editar Integração' : 'Nova Integração'}
                </h1>
            </div>

            <div>
                Todas as informações de chamadas de integrações poderão ser
                visualizadas nos atendimentos
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Steps Navigation */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <FormSteps
                            steps={steps}
                            currentStep={currentStep}
                            onStepClick={handleStepClick}
                        />
                    </div>
                </div>

                {/* Step 1: Informações Básicas */}
                {currentStep === 1 && (
                    <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                        <div className="card-body">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text !text-black">
                                        Nome{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Nome da integração"
                                    className="input input-bordered w-full"
                                    value={formData.name}
                                    onChange={e =>
                                        updateFormField('name', e.target.value)
                                    }
                                    required
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label !justify-start !items-start">
                                    <span className="label-text !text-black">
                                        Contexto{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                    <span className="label-text !text-black-alt break-words whitespace-normal w-full">
                                        Descreva claramente o assunto que
                                        acionará esta integração
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder={`Ex: Quando o ${clientLabel} falar sobre consultar pedidos, status de entrega, rastreamento de produtos...`}
                                    value={formData.context}
                                    onChange={e =>
                                        updateFormField(
                                            'context',
                                            e.target.value
                                        )
                                    }
                                    rows={5}
                                    required
                                />
                            </div>

                            {/*
                               <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text !text-black">
                                        Descrição{' '}
                                        <span className="text-gray-400 text-sm">
                                            (opcional)
                                        </span>
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Descrição da integração"
                                    value={formData.description}
                                    onChange={e =>
                                        updateFormField(
                                            'description',
                                            e.target.value
                                        )
                                    }
                                    rows={3}
                                />
                            </div>
                               */}
                        </div>
                    </div>
                )}

                {/* Step 2: Primeira Chamada - URL e Propriedades */}
                {currentStep === 2 && (
                    <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                        <div className="card-body">
                            <div className="flex items-center gap-2 mb-4">
                                <MdLink className="text-xl text-secondary" />
                                <h2 className="card-title text-lg">
                                    URL externa e Propriedades
                                </h2>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text !text-black">
                                        URL Externa{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="url"
                                    placeholder="https://api.example.com/v1/endpoint"
                                    className="input input-bordered w-full"
                                    value={formData.externalUrl}
                                    onChange={e =>
                                        updateFormField(
                                            'externalUrl',
                                            e.target.value
                                        )
                                    }
                                    required
                                />
                            </div>

                            <div className="form-control w-full border border-gray-200 rounded-lg p-4">
                                <label className="label flex-wrap">
                                    <span className="label-text !text-black flex items-center gap-2">
                                        <MdCode />
                                        Propriedades{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <div className="label-text !text-black-alt break-words whitespace-normal w-full mb-2">
                                    Dados que a IA solicitará ao {clientLabel} via
                                    WhatsApp antes de fazer a chamada (ex: CPF,
                                    email, número do pedido, etc.)
                                </div>
                                <PropertiesFields
                                    properties={formData.properties}
                                    onChange={properties =>
                                        updateFormField(
                                            'properties',
                                            properties
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full border border-gray-200 rounded-lg p-4">
                                <div className="label flex-wrap">
                                    <div className="label-text !text-black flex items-center gap-2">
                                        <MdCode />
                                        Headers HTTP{' '}
                                        <span className="text-gray-400 text-sm">
                                            (opcional)
                                        </span>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 h-auto min-h-0 text-secondary"
                                        onClick={() =>
                                            setShowHeaders(!showHeaders)
                                        }
                                    >
                                        {showHeaders ? 'Fechar' : 'Adicionar'}{' '}
                                        {!showHeaders ? (
                                            <BsChevronDown />
                                        ) : (
                                            <BsChevronUp />
                                        )}
                                    </button>
                                </div>
                                <div className="label-text !text-gray-500 mb-2">
                                    Headers HTTP customizados para a requisição
                                    (ex: Content-Type, Accept, etc.)
                                </div>
                                {showHeaders && (
                                    <PropertiesFields
                                        properties={formData.headers}
                                        onChange={headers =>
                                            updateFormField('headers', headers)
                                        }
                                        prefix="headers"
                                        buttonText="Adicionar Header"
                                        mode="keyValue"
                                    />
                                )}
                            </div>

                            <div className="form-control w-full border border-gray-200 rounded-lg p-4">
                                <div className="label flex-wrap">
                                    <div className="label-text !text-black flex items-center gap-2">
                                        <MdCode />
                                        Params (Query String){' '}
                                        <span className="text-gray-400 text-sm">
                                            (opcional)
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 h-auto min-h-0 text-secondary"
                                        onClick={() =>
                                            setShowParams(!showParams)
                                        }
                                    >
                                        {showParams ? 'Fechar' : 'Adicionar'}{' '}
                                        {!showParams ? (
                                            <BsChevronDown />
                                        ) : (
                                            <BsChevronUp />
                                        )}
                                    </button>
                                </div>
                                <div className="label-text !text-gray-500 mb-2">
                                    Parâmetros de query string para a URL (ex:
                                    page, limit, filter, etc.)
                                </div>
                                {showParams && (
                                    <PropertiesFields
                                        properties={formData.params}
                                        onChange={params =>
                                            updateFormField('params', params)
                                        }
                                        prefix="params"
                                        buttonText="Adicionar Parâmetro"
                                        mode="keyValue"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 3: Primeira Chamada - Autenticação */}
                {currentStep === 3 && (
                    <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                        <div className="card-body">
                            <div className="flex items-center gap-2 mb-4">
                                <MdLock className="text-xl text-secondary" />
                                <h2 className="card-title text-lg">
                                    Autenticação
                                </h2>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text !text-black flex items-center gap-2">
                                        <MdLock />
                                        Tipo de Autenticação
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.authenticationType}
                                    onChange={e => {
                                        updateFormField(
                                            'authenticationType',
                                            e.target.value as AuthenticationType
                                        )
                                        // Limpar dados de autenticação ao mudar o tipo
                                        updateFormField(
                                            'authenticationData',
                                            {}
                                        )
                                    }}
                                >
                                    <option value={AuthenticationType.NONE}>
                                        Nenhuma
                                    </option>
                                    <option value={AuthenticationType.BASIC}>
                                        Basic Auth
                                    </option>
                                    <option value={AuthenticationType.BEARER}>
                                        Bearer Token
                                    </option>
                                    <option value={AuthenticationType.API_KEY}>
                                        API Key
                                    </option>
                                    <option value={AuthenticationType.OAUTH2}>
                                        OAuth2
                                    </option>
                                    <option
                                        value={
                                            AuthenticationType.CLIENT_CREDENTIALS
                                        }
                                    >
                                        Client Credentials
                                    </option>
                                    <option value={AuthenticationType.JWT}>
                                        JWT
                                    </option>
                                </select>
                            </div>

                            <AuthenticationFields
                                authenticationType={formData.authenticationType}
                                authenticationData={formData.authenticationData}
                                onChange={data =>
                                    updateFormField('authenticationData', data)
                                }
                            />
                        </div>
                    </div>
                )}

                {/* Step 4: Primeira Chamada - O que fazer com a resposta */}
                {currentStep === 4 && (
                    <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                        <div className="card-body">
                            <h2 className="card-title text-lg mb-4">
                                O que fazer com a resposta
                            </h2>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text !text-black">
                                        O que fazer com a resposta
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.whatToDoWithResponse}
                                    onChange={e => {
                                        updateFormField(
                                            'whatToDoWithResponse',
                                            e.target
                                                .value as WhatToDoWithTheResponse
                                        )
                                    }}
                                >
                                    <option
                                        value={
                                            WhatToDoWithTheResponse.USE_TO_ANSWER_CLIENT
                                        }
                                    >
                                        Salvar e responder ao {clientLabel}
                                    </option>
                                    <option
                                        value={
                                            WhatToDoWithTheResponse.SAVE_IN_CHAT
                                        }
                                    >
                                        Apenas salvar no atendimento
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex gap-2 pt-4 mb-5">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/integrations')}
                    >
                        Cancelar
                    </button>
                    <div className="flex-1 flex gap-2 justify-end">
                        {currentStep > 1 && (
                            <button
                                type="button"
                                className="btn btn-outline gap-2"
                                onClick={handleBack}
                            >
                                <MdBack className="text-lg" />
                                Anterior
                            </button>
                        )}
                        {currentStep < totalSteps ? (
                            <button
                                type="button"
                                className="btn btn-secondary gap-2"
                                onClick={handleNext}
                                disabled={!canGoToNextStep()}
                            >
                                Próximo
                                <MdArrowForward className="text-lg" />
                            </button>
                        ) : (
                            <button
                                type="submit"
                                className="btn btn-secondary"
                                disabled={
                                    loading ||
                                    !formData.name.trim() ||
                                    !formData.context.trim()
                                }
                            >
                                {loading ? (
                                    <span className="loading loading-spinner"></span>
                                ) : isEditing ? (
                                    'Atualizar'
                                ) : (
                                    'Salvar'
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    )
}
